export const BOARD_CONFIG = {
  rows: 15,
  columns: 15,
  boardWidth: 1000,
  boardHeight: 1000,
  borderSize: 6,
  gridLineWidth: 6, // Use even number to avoid subpixel rendering issues
  hud: {
    powerupsSectionHeight: 120,
    sectionSeparatorWidth: 4, // Use even number to avoid subpixel rendering issues
  },
  headTilePadding: 8, // Pixels to subtract from tile size for head
  bodyTilePadding: 14, // Pixels to subtract from tile size for body

  get tileWidth() {
    return Math.floor(this.boardWidth / this.columns);
  },
  get tileHeight() {
    return Math.floor(this.boardHeight / this.rows);
  },

  get totalGridWidth() {
    // Use actual tile width (which may be floored) instead of boardWidth
    return (
      (this.columns + 1) * this.gridLineWidth + this.columns * this.tileWidth
    );
  },
  get totalGridHeight() {
    // Use actual tile height (which may be floored) instead of boardHeight
    return (this.rows + 1) * this.gridLineWidth + this.rows * this.tileHeight;
  },
  get totalHudHeight() {
    return this.hud.powerupsSectionHeight;
  },

  /** Height of the name-bar strip above each board panel. */
  nameBarHeight: 80,

  /** Width of one board panel (grid + both side borders). */
  get totalBoardWidth() {
    return 2 * this.borderSize + this.totalGridWidth;
  },

  /**
   * Height of the board content area, excluding the name bar. The HUD is
   * attached directly below the grid — top border + grid + shared separator
   * line + HUD + bottom border.
   */
  get boardContentHeight() {
    return this.totalGridHeight + this.totalHudHeight + 3 * this.borderSize;
  },

  /** Total height of one board panel including the name bar. */
  get totalBoardHeight() {
    return this.nameBarHeight + this.boardContentHeight;
  },

  /**
   * Width of the in-canvas left rail column (clock, players, longest snake).
   */
  railWidth: 520,

  /**
   * Gap between canvas columns: rail ↔ player board ↔ opponent column.
   */
  boardGap: 100,

  /**
   * Fixed render scale for opponent board content. The same scale is used for
   * every player count (2–4) so opponent boards always have the same size;
   * chosen so three opponents plus margins fit within one board height.
   */
  opponentScale: 0.31,

  /** Vertical gap between stacked opponent boards. */
  opponentMargin: 16,

  /**
   * Fixed height of an opponent board's header bar. Headers are drawn by the
   * overlay scene at canvas scale (not through the zoomed board cameras) so
   * their text stays readable.
   */
  opponentHeaderHeight: 44,

  /** Total height of one opponent slot (fixed header + scaled content). */
  get opponentSlotHeight() {
    return (
      this.opponentHeaderHeight +
      Math.round(this.boardContentHeight * this.opponentScale)
    );
  },

  /** Corner cut size for notched panel borders (name bar / HUD sections). */
  cornerNotch: 20,

  /** Rendered width of an opponent board (content at opponentScale). */
  get opponentBoardWidth() {
    return Math.ceil(this.totalBoardWidth * this.opponentScale);
  },

  /**
   * Total canvas width: rail | gap | player board | gap | opponents | margin.
   * Both side columns reserve `railWidth`, keeping the player board exactly
   * centred; the opponent boards sit at `boardGap` from the board (same
   * spacing as the rail side) with the remainder as right margin.
   */
  get canvasWidth() {
    return 2 * this.railWidth + 2 * this.boardGap + this.totalBoardWidth;
  },
} as const;

/** Maximum Manhattan distance for food spawn. Derived from board columns (round(columns * 0.8)). */
export const FOOD_DISTANCE_MAX = Math.round(BOARD_CONFIG.columns * 0.8);

/** Maximum allowed min-snake-segments setting. Derived from board rows (round(rows / 2)). */
export const MIN_SNAKE_SEGMENTS_MAX = Math.round(BOARD_CONFIG.rows / 2);
